import { z } from "zod";

import { ListProcessingStatus } from "@/enum/list.enum";

export enum DashboardIssueType {
	LINKEDIN_DISCONNECTED = "LINKEDIN_DISCONNECTED",
	LINKEDIN_SN_DISCONNECTED = "LINKEDIN_SN_DISCONNECTED",
	CRM_SYNC_FAILED = "CRM_SYNC_FAILED",
	LIST_NO_RESULTS_ONGOING = "LIST_NO_RESULTS_ONGOING",
	LIST_NO_RESULTS_ONCE = "LIST_NO_RESULTS_ONCE",
	LIST_OUTREACH_NOT_ACTIVATED = "LIST_OUTREACH_NOT_ACTIVATED",
	CREDITS_LOW = "CREDITS_LOW",
	EMAIL_LOGGED_OUT = "EMAIL_LOGGED_OUT",
	COMPETITORS_NEED_REVIEW = "COMPETITORS_NEED_REVIEW",
	/** HubSpot notes waiting because their sender has no HubSpot owner (PREC-3984). */
	HUBSPOT_NOTE_OWNER_MISSING = "HUBSPOT_NOTE_OWNER_MISSING",
	/** A campaign paused because a lead could not be sent for a reason in the campaign (PREC-4059). */
	CAMPAIGN_PAUSED_UNSENDABLE = "CAMPAIGN_PAUSED_UNSENDABLE",
	/** Dynamics users refused as the owner of synced records for a missing privilege (PREC-4361). */
	DYNAMICS_OWNER_PRIVILEGE_MISSING = "DYNAMICS_OWNER_PRIVILEGE_MISSING",
	/** A CRM no longer accepts the stored sign-in; nothing syncs until it is reconnected (PREC-4353). */
	CRM_RECONNECT_REQUIRED = "CRM_RECONNECT_REQUIRED",
	/** Zoho refuses to search the field the LinkedIn URL is mapped onto (PREC-4354). */
	ZOHO_LINKEDIN_FIELD_NOT_SEARCHABLE = "ZOHO_LINKEDIN_FIELD_NOT_SEARCHABLE",
}

export type DashboardIssueSeverity = "warning" | "error";

// Slim projection served by the dashboard's preview sections — exactly what
// `personRowFields` renders (identity, organization, list, recency). The API
// deliberately no longer sends full ListPerson rows here (perf: the full
// include tree saturated the DB on every dashboard load).
export const DashboardRepliesConversationSchema = z.object({
	id: z.number(),
	sentiment: z.string().nullable().optional(),
	message: z.string().nullable().optional(),
	sentAt: z.union([z.string(), z.date()]).nullable().optional(),
	// Not sent by the dashboard endpoint (only schedule endpoints compute it);
	// kept optional so `latestConversation`'s preference check stays typed.
	wasLastConversation: z.boolean().nullable().optional(),
});

export const DashboardRepliesItemSchema = z.object({
	id: z.number(),
	updatedAt: z.union([z.string(), z.date()]),
	prospect: z
		.object({
			id: z.number(),
			firstName: z.string().nullable().optional(),
			lastName: z.string().nullable().optional(),
			name: z.string().nullable().optional(),
			organization: z
				.object({
					id: z.number().optional(),
					name: z.string().nullable().optional(),
				})
				.nullable()
				.optional(),
			// Company-scoped, sentAt ASC — the last entry is the latest reply.
			conversations: z.array(DashboardRepliesConversationSchema).optional(),
		})
		.nullable()
		.optional(),
	list: z.object({
		id: z.number(),
		name: z.string(),
	}),
});

export const DashboardRepliesSectionSchema = z.object({
	items: z.array(DashboardRepliesItemSchema),
	total: z.number(),
});

export const DashboardIssueSchema = z.object({
	type: z.nativeEnum(DashboardIssueType),
	severity: z.union([z.literal("warning"), z.literal("error")]),
	entityId: z.number().nullable(),
	payload: z.record(z.string(), z.unknown()).optional(),
	detectedAt: z.string(),
});

export const DashboardFirstStepsSchema = z.object({
	hasCreatedOneList: z.boolean(),
	hasConnectedLinkedinProfile: z.boolean(),
	hasConnectedOneEMail: z.boolean(),
	hasCreatedOneProfileTrackerList: z.boolean(),
	hasReviewedAndActivatedTheLeadScoring: z.boolean(),
	hasReviewedTheDeepResearchPrompt: z.boolean(),
	hasTestedTheDeepResearchPromptOnOneProspect: z.boolean(),
	hasCreatedFirstCampaign: z.boolean(),
	// Optional so a web build that ships ahead of the API doesn't fail the whole
	// dashboard parse on a missing flag — an absent flag just keeps the row open.
	hasReachedOutToFirstLeads: z.boolean().optional(),
	hasConnectedSignalistToOneCrm: z.boolean(),
	// Optional for the same web-ahead-of-API reason; absent keeps the promo visible.
	hasInstalledBrowserExtension: z.boolean().optional(),
});

export const DashboardListProspectPreviewSchema = z.object({
	id: z.number(),
	listPersonId: z.number(),
	firstName: z.string().nullable(),
	lastName: z.string().nullable(),
	title: z.string().nullable(),
	image: z.string().nullable(),
});

export const DashboardListWithNewProspectsSchema = z.object({
	id: z.number(),
	name: z.string(),
	newProspectCount: z.number(),
	processingStatus: z.nativeEnum(ListProcessingStatus),
	processingStatusMessage: z.string().nullable(),
	processingProgressPercent: z.number().nullable().optional(),
	prospects: z.array(DashboardListProspectPreviewSchema),
});

export const DashboardResponseSchema = z.object({
	myReplies: DashboardRepliesSectionSchema,
	companyReplies: DashboardRepliesSectionSchema,
	// Company-wide replied leads that have gone cold (re-engage candidates).
	reEngage: DashboardRepliesSectionSchema.optional(),
	newDrafts: DashboardRepliesSectionSchema,
	listsWithNewProspects: z.array(DashboardListWithNewProspectsSchema),
	issues: z.array(DashboardIssueSchema),
	firstSteps: DashboardFirstStepsSchema,
});

export type DashboardRepliesItem = z.infer<typeof DashboardRepliesItemSchema>;
export type DashboardRepliesSection = z.infer<typeof DashboardRepliesSectionSchema>;
export type DashboardListProspectPreview = z.infer<typeof DashboardListProspectPreviewSchema>;
export type DashboardListWithNewProspects = z.infer<typeof DashboardListWithNewProspectsSchema>;
export type DashboardIssue = z.infer<typeof DashboardIssueSchema>;
export type DashboardFirstSteps = z.infer<typeof DashboardFirstStepsSchema>;
export type DashboardResponse = z.infer<typeof DashboardResponseSchema>;
export type DashboardFirstStepKey = keyof DashboardFirstSteps;

export interface DashboardIssueLinkedinPayload {
	unipileStatus: string | null;
}

export interface DashboardIssueCrmSyncPayload {
	total: number;
	counts: { prospects: number; orgs: number; leads: number; deals: number };
}

export interface DashboardIssueHubspotNoteOwnerMissingPayload {
	conversations: number;
	senders: { id: number; name: string }[];
}

export interface DashboardIssueDynamicsOwnerPrivilegePayload {
	owners: { ownerId: string; ownerName: string | null; privileges: string[]; records: number }[];
	windowDays: number;
}

export interface DashboardIssueCampaignPausedPayload {
	listId: number;
	listName: string;
	campaignUuid: string;
	campaignName: string;
	/** The API's `CampaignPauseReason`; an unknown one still renders, with generic copy. */
	reason: string;
	detail: Record<string, unknown>;
}

export interface DashboardIssueListNoResultsPayload {
	listName: string;
}

export interface DashboardIssueOutreachNotActivatedPayload {
	listName: string;
	assignedCount: number;
}

export interface DashboardIssueCreditsLowPayload {
	remaining: number;
	cycleCredits: number;
	threshold: number;
	perMonthOrYear: string;
	nextCycle: string;
}

export interface DismissIssueRequest {
	type: DashboardIssueType;
	entityId?: number | null;
}
